import React, { useEffect, useState, useRef } from "react";
import { getCountries, getCountryCallingCode } from "react-phone-number-input";
import countryNames from "react-phone-number-input/locale/en.json";
import countryData from "./countyCodePlaceholder.json";
import { ArrowDownIcon } from "icons/payments/ArrowDownIcon";
import { SearchIcon } from "icons/payments/SearchIcon";
import useOutsideClick from "hooks/useOutsideClick";

const DEFAULT_COUNTRY_CODE = "US";
const defaultCountryInfo = countryData.find(
  (country) => country.countryCode === DEFAULT_COUNTRY_CODE
);

if (!defaultCountryInfo) {
  throw new Error(`Country ${DEFAULT_COUNTRY_CODE} not found`);
}

const [defaultCallingCode, ...defaultNumberParts] =
  defaultCountryInfo.formattedNumber.split(" ");
const DEFAULT_COUNTRY = {
  countryCode: defaultCountryInfo.countryCode,
  ccode: defaultCallingCode.slice(1),
  placeholder: defaultNumberParts.join(" ").trim(),
};

function getNationalInputValue(
  value: string | null | undefined,
  selectedCountry: string
) {
  if (!value) return "";

  const legacyValue = value.match(/^\+\((\d+)\)(.*)$/);
  if (legacyValue) {
    return legacyValue[2]?.trim() ?? "";
  }

  if (value.startsWith("+")) {
    const digits = value.replace(/\D/g, "");
    const callingCode = getCountryCallingCode(selectedCountry as any);

    return digits.startsWith(callingCode)
      ? digits.slice(callingCode.length)
      : digits;
  }

  return value;
}

function formatNumber(countryCode: string, inputValue: string) {
  if (inputValue?.length <= 0) return "";

  const country = countryData.find(
    (country) => country.countryCode === countryCode
  );

  if (!country) return inputValue;

  const [, ...numberParts] = country.formattedNumber.split(" ");
  const placeholderNumber = numberParts.join(" ").trim();

  const format = placeholderNumber || DEFAULT_COUNTRY.placeholder;
  const digits = inputValue?.replace(/\D/g, "");

  let formattedString = "";
  let digitIndex = 0;

  for (let char of format) {
    if (char.match(/\d/)) {
      if (digitIndex < digits?.length) {
        formattedString += digits?.[digitIndex++];
      } else {
        break;
      }
    } else {
      formattedString += char;
    }
  }

  return formattedString;
}

interface MobileInputNewProps {
  userCountry: string;
  setInputValue: (value: string) => void;
  setCountry: (country: string) => void;
  inputValue?: string | null;
  countryCode?: string;
  _className?: string;
  clearValue?: boolean;
  phoneClassName?: string;
  isProductLinkInput?: boolean;
  hasError?: boolean;
  onBlur?: () => void;
  "data-testid"?: string;
  "aria-label"?: string;
}

const MobileInputNew: React.FC<MobileInputNewProps> = ({
  userCountry,
  setInputValue,
  setCountry,
  inputValue = null,
  countryCode,
  _className = "",
  clearValue = false,
  phoneClassName = "",
  isProductLinkInput = false,
  hasError = false,
  onBlur,
  ...inputProps
}) => {
  const borderClass = hasError
    ? "border-red"
    : "border-strokePrimary group-hover:border-strokeActive";
  const [isOpen, setIsOpen] = useState(false);
  const [dropUp, setDropUp] = useState(false);
  const [countrySearchInput, setCountrySearchInput] = useState("");
  const [countries, setCountries] = useState(getCountries());
  const dropdownRef = useRef<HTMLDivElement>(null);

  useOutsideClick(dropdownRef, () => {
    setIsOpen(false);
    setCountrySearchInput("");
  });

  // Match InputSelect: open upwards when there isn't room below, otherwise the
  // 245-country list runs off the bottom of a phone screen and only the search
  // box is reachable without scrolling the page.
  useEffect(() => {
    if (!isOpen) return;
    const rect = dropdownRef.current?.getBoundingClientRect();
    if (!rect) return;
    const dropdownHeight = 330;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    setDropUp(spaceBelow < dropdownHeight && spaceAbove > spaceBelow);
  }, [isOpen]);

  const [phoneNumber, setPhoneNumber] = useState({
    number: inputValue ?? "",
    placeholder: DEFAULT_COUNTRY.placeholder,
    ccode: countryCode?.replace(/\D/g, "") ?? DEFAULT_COUNTRY.ccode,
    countryCode: userCountry,
  });

  useEffect(() => {
    const countryInfo = countryData.find(
      (country) => country.countryCode === userCountry
    );
    if (!countryInfo) return;

    const [, ...numberParts] = countryInfo.formattedNumber.split(" ");
    const placeholderNumber = numberParts.join(" ").trim();

    const inputValueNumberParts = getNationalInputValue(
      inputValue,
      userCountry
    );

    setPhoneNumber({
      number: formatNumber(userCountry, inputValueNumberParts),
      placeholder: placeholderNumber ?? DEFAULT_COUNTRY.placeholder,
      ccode:
        countryCode?.replace(/\D/g, "") ??
        getCountryCallingCode(userCountry as any),
      countryCode: userCountry,
    });
  }, [countryCode, inputValue, userCountry]);

  // useEffect(() => {
  //   if (phoneNumber?.number?.includes('+')) return;
  //   setInputValue(`+(${phoneNumber.ccode})${phoneNumber.number}`);
  // }, [phoneNumber.ccode, phoneNumber.number]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, selectionStart } = event.target;

    const isSymbol = (val: string) => !/\d/.test(val);

    if (
      (event.nativeEvent as InputEvent).inputType === "deleteContentBackward"
    ) {
      if (
        selectionStart !== null &&
        isSymbol(phoneNumber.number[selectionStart]) &&
        value.length !== selectionStart
      ) {
        setPhoneNumber((prevState) => ({
          ...prevState,
          number: phoneNumber.number,
        }));
        setInputValue(`+(${phoneNumber.ccode})${phoneNumber.number}`);
        return;
      } else {
        if (
          selectionStart !== null &&
          isSymbol(phoneNumber.number[selectionStart - 1])
        ) {
          if (value.length !== selectionStart) {
            setPhoneNumber((prevState) => ({ ...prevState, number: value }));
            setInputValue(`+(${phoneNumber.ccode})${value}`);
            return;
          } else {
            setPhoneNumber((prevState) => ({
              ...prevState,
              number: value.slice(0, -1),
            }));
            setInputValue(`+(${phoneNumber.ccode})${value.slice(0, -1)}`);
            return;
          }
        } else {
          setPhoneNumber((prevState) => ({ ...prevState, number: value }));
          setInputValue(`+(${phoneNumber.ccode})${value}`);
          return;
        }
      }
    } else {
      const formatted = formatNumber(phoneNumber.countryCode, value);
      setPhoneNumber((prevState) => ({ ...prevState, number: formatted }));
      setInputValue(`+(${phoneNumber.ccode})${formatted}`);
    }
  };

  useEffect(() => {
    const names = countryData;
    let filteredData: string[];

    filteredData = names
      .filter((el) => {
        if (parseInt(countrySearchInput)) {
          const [ccode] = el.formattedNumber.split(" ");
          return (
            ccode.replace(/\+/g, "") === countrySearchInput.replace(/\+/g, "")
          );
        } else {
          const lowerCaseEl = el.countryCode.toLowerCase();
          const lowerCasecountrySearchInput = countrySearchInput.toLowerCase();

          return (
            lowerCaseEl.includes(lowerCasecountrySearchInput) ||
            (countryNames as any)[el.countryCode]
              ?.toLowerCase()
              .includes(lowerCasecountrySearchInput)
          );
        }
      })
      .map((el) => el.countryCode);

    setCountries(filteredData as any);
  }, [countrySearchInput]);

  const handleCountrySelect = (countryCode: string) => {
    const country = countryData.find(
      (country) => country.countryCode === countryCode
    );

    if (!country) return;

    const [ccode, ...numberParts] = country.formattedNumber.split(" ");
    const placeholder = numberParts.join(" ").trim();

    setPhoneNumber({
      number: "",
      placeholder,
      ccode: ccode.slice(1),
      countryCode: country.countryCode,
    });

    setCountry(country.countryCode);
    if (!clearValue) {
      setInputValue("");
    }
    setCountrySearchInput("");
  };

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <div
        style={{
          display: !isOpen ? "none" : "block",
          boxShadow: "0px 0px 10px 0px #00000026",
        }}
        className={`absolute z-30 w-full ${dropUp ? "bottom-[calc(100%-4px)]" : "top-[36px]"} border border-strokePrimary rounded-[7px] left-0 bg-bgPrimary ${_className}`}
      >
        <div className="rounded-[7px] relative">
          <input
            placeholder="Search country"
            className="text-base bg-bgPrimary rounded-[7px] border-b-strokePrimary font-normal border-b text-txtPrimary h-10 w-full pl-10 focus:outline-none placeholder:text-strokePrimary"
            name="countrySearchInput"
            value={countrySearchInput}
            onChange={(e) => setCountrySearchInput(e.target.value)}
          />
          <div className="absolute top-[11px] left-4">
            <SearchIcon />
          </div>
        </div>
        <div className="bg-bgPrimary px-[15px] py-1.5 h-fit max-h-[288px] overflow-y-auto">
          {countries &&
            countries.map((itm, index) => (
              <div
                key={`country-${itm}`}
                role="option"
                aria-selected={itm === phoneNumber.countryCode}
                data-testid={
                  inputProps["data-testid"]
                    ? `${inputProps["data-testid"]}-country-option-${itm.toLowerCase()}`
                    : undefined
                }
                className="relative flex cursor-pointer justify-between px-2.5 py-2 text-blue h-8 rounded-[7px] mb-2 hover:bg-bgHover"
                onClick={() => {
                  setIsOpen(false);
                  handleCountrySelect(itm || undefined);
                }}
              >
                <div className="flex gap-2 items-center">
                  <img
                    className="w-[28px]"
                    src={`https://purecatamphetamine.github.io/country-flag-icons/3x2/${itm}.svg`}
                    alt=""
                  />
                  <div className="text-xs text-txtPrimary font-medium">
                    {countryNames[itm]}
                  </div>
                </div>
                <div className="text-sm text-txtPrimary">
                  {"+"}
                  {getCountryCallingCode(itm)}
                </div>
              </div>
            ))}
        </div>
      </div>

      <div className="w-full group">
        <div className="flex w-full">
          <div
            onClick={() => setIsOpen(!isOpen)}
            role="button"
            tabIndex={0}
            aria-label="Select phone country code"
            aria-expanded={isOpen}
            data-testid={
              inputProps["data-testid"]
                ? `${inputProps["data-testid"]}-country-button`
                : undefined
            }
            className={`flex cursor-pointer font-normal ${isProductLinkInput ? "py-2 px-3" : "p-3"} border-l border-y border-r-0 gap-1 ${borderClass} items-center rounded-l-md transition-colors`}
          >
            <img
              className="w-[24px] h-4"
              src={`https://purecatamphetamine.github.io/country-flag-icons/3x2/${userCountry}.svg`}
              alt=""
            />
            <span
              className={`transform transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
            >
              <ArrowDownIcon />
            </span>
          </div>
          <div
            className={`flex items-center gap-1.5 border-r border-t border-b ${borderClass} rounded-r-md py-1.75 px-1.5 w-full bg-bgPrimary transition-colors`}
          >
            <span
              className={`${isProductLinkInput ? "text-xs" : "text-sm"} text-txtPrimary`}
            >{`+${phoneNumber.ccode}`}</span>
            <input
              className={`w-full bg-bgPrimary text-txtPrimary outline-none ${isProductLinkInput ? "text-xs" : "text-sm"} ${phoneClassName}`}
              placeholder={phoneNumber.placeholder}
              value={phoneNumber.number}
              onChange={handleChange}
              onBlur={onBlur}
              data-testid={inputProps["data-testid"]}
              aria-label={inputProps["aria-label"] || "Phone number"}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileInputNew;
